import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, RefreshCw, Save, ChevronDown, ChevronRight,
  Sparkles, DollarSign, Loader2
} from 'lucide-react';
import { syncPpcModels } from '../api/fetching';
import { listPpcModels, batchUpdatePpcOrder, patchModel } from '../api/models';
import type {
  AdminModelView,
  SyncPpcModelsResponse,
  SyncPromoteModelItem,
} from '../api/types';
import PageHeader from '../components/shared/PageHeader';
import { EmptyState } from '../components/shared/EmptyState';
import { TableSkeleton } from '../components/shared/TableSkeleton';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { useDirtyState } from '../hooks/useDirtyState';

const SYNC_COLLAPSED_KEY = 'ppc-sync-collapsed';

// ─── Sortable Row for Global PPC ordering ───
const SortablePpcRow: React.FC<{
  model: AdminModelView;
  index: number;
  onPpcTypeChange: (id: number, newType: string) => void;
  isUpdating: boolean;
}> = ({ model, index, onPpcTypeChange, isUpdating }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(model.id) });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? 'var(--accent-soft)' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'selected' : undefined}>
      <td
        {...attributes}
        {...listeners}
        style={{ width: 36, padding: '6px 4px' }}
        aria-label={`Drag to reorder ${model.modelUsername}`}
      >
        <span className="hf-grip" title="Drag to reorder">
          <GripVertical size={14} />
        </span>
      </td>
      <td className="num" style={{ width: 48, color: 'var(--fg-3)' }}>
        <span className="hf-mono">{index + 1}</span>
      </td>
      <td><span className="hf-mono">@{model.modelUsername}</span></td>
      <td style={{ color: 'var(--fg-2)' }}>{model.modelName || '—'}</td>
      <td className="num" style={{ width: 80, color: 'var(--fg-3)' }}>
        <span className="hf-mono">{model.ppcOrder ?? '—'}</span>
      </td>
      <td style={{ width: 130 }}>
        <select
          value={model.ppcType || 'REGULAR'}
          onChange={(e) => onPpcTypeChange(model.id, e.target.value)}
          disabled={isUpdating}
          className="hf-select"
          aria-label={`PPC type for ${model.modelUsername}`}
        >
          <option value="REGULAR">REGULAR</option>
          <option value="TS">TS</option>
        </select>
      </td>
    </tr>
  );
};

const PpcPage: React.FC = () => {
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncPpcModelsResponse | null>(null);
  const [syncCollapsed, setSyncCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SYNC_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SYNC_COLLAPSED_KEY, syncCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [syncCollapsed]);

  const [ppcModels, setPpcModels] = useState<AdminModelView[]>([]);
  const [ppcLoading, setPpcLoading] = useState(false);
  const [ppcTab, setPpcTab] = useState<'REGULAR' | 'TS'>('REGULAR');
  const [ppcSaving, setPpcSaving] = useState(false);
  const [ppcDirty, setPpcDirty] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // beforeunload guard when reorder is unsaved
  useDirtyState({ d: ppcDirty }, { d: false });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { loadPpcModels(); }, []);

  const loadPpcModels = async () => {
    setPpcLoading(true);
    try {
      const result = await listPpcModels();
      setPpcModels(result.items || []);
      setPpcDirty(false);
    } catch (err) {
      console.error('Failed to load PPC models', err);
      setError('Failed to load PPC models');
    } finally {
      setPpcLoading(false);
    }
  };

  const handleSync = async () => {
    if (syncLoading) return;
    if (ppcDirty) {
      const ok = window.confirm('You have unsaved order changes. Sync will reload — discard changes?');
      if (!ok) return;
    }
    setSyncLoading(true);
    setError(null);
    try {
      const result = await syncPpcModels();
      setSyncResult(result);
      setSyncCollapsed(false);
      await loadPpcModels();
    } catch (err) {
      console.error('PPC sync failed', err);
      setError('PPC sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  const filteredPpcModels = ppcModels.filter((m) => (m.ppcType || 'REGULAR') === ppcTab);

  const handlePpcDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredPpcModels.findIndex((m) => String(m.id) === active.id);
    const newIndex = filteredPpcModels.findIndex((m) => String(m.id) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(filteredPpcModels, oldIndex, newIndex);
    const updatedIds = new Map<number, number>();
    reordered.forEach((m, i) => updatedIds.set(m.id, i + 1));

    setPpcModels((prev) =>
      prev.map((m) => (updatedIds.has(m.id) ? { ...m, ppcOrder: updatedIds.get(m.id)! } : m))
    );
    setPpcDirty(true);
  };

  const handleSavePpcOrder = async () => {
    setPpcSaving(true);
    setError(null);
    try {
      const items = ppcModels
        .filter((m) => m.ppcOrder != null)
        .map((m) => ({ id: m.id, ppcOrder: m.ppcOrder! }));
      await batchUpdatePpcOrder(items);
      setPpcDirty(false);
    } catch (err) {
      console.error('Failed to save PPC order', err);
      setError('Failed to save PPC order');
    } finally {
      setPpcSaving(false);
    }
  };

  const handlePpcTypeChange = async (id: number, newType: string) => {
    setUpdating(true);
    try {
      await patchModel(id, { ppcType: newType });
      setPpcModels((prev) => prev.map((m) => (m.id === id ? { ...m, ppcType: newType } : m)));
    } catch (err) {
      console.error('Failed to update ppc type', err);
      setError('Failed to update PPC type');
    } finally {
      setUpdating(false);
    }
  };

  const renderModelsSection = (title: string, models: SyncPromoteModelItem[]) => (
    <div style={{ marginBottom: 14 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {title} {models.length > 0 ? `· ${models.length}` : ''}
      </h4>
      {models.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 12.5 }}>No changes.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {models.map((m) => (
            <span key={m.id} className="hf-tag outline sm">@{m.modelUsername}</span>
          ))}
        </div>
      )}
    </div>
  );

  const ppcCounts = {
    REGULAR: ppcModels.filter((m) => (m.ppcType || 'REGULAR') === 'REGULAR').length,
    TS: ppcModels.filter((m) => m.ppcType === 'TS').length,
  };

  return (
    <>
      <PageHeader
        title="PPC Ordering"
        subtitle={
          <>
            Drag rows to rearrange · changes preview before save
            {ppcDirty && <span className="hf-tag warning sm" style={{ marginLeft: 8 }}>unsaved changes</span>}
          </>
        }
        actions={
          <>
            <button className="hf-btn" onClick={loadPpcModels} disabled={ppcLoading}>
              <RefreshCw size={14} className={ppcLoading ? 'spin' : undefined} /> Reload
            </button>
            <button className="hf-btn primary" onClick={handleSync} disabled={syncLoading}>
              {syncLoading
                ? <><Loader2 size={14} className="spin" /> Syncing…</>
                : <><DollarSign size={14} /> Sync PPC models</>}
            </button>
          </>
        }
      />
      <div className="hf-content">

        {error && (
          <ErrorBanner
            kind="danger"
            message={error}
            onDismiss={() => setError(null)}
            onRetry={() => { setError(null); void loadPpcModels(); }}
          />
        )}

        {/* Sync Results */}
        {syncResult && (
          <div className="hf-card">
            <button
              type="button"
              className="hf-card-header"
              style={{
                width: '100%', cursor: 'pointer', background: 'transparent',
                border: 'none', font: 'inherit', color: 'inherit', textAlign: 'left'
              }}
              onClick={() => setSyncCollapsed(!syncCollapsed)}
              aria-expanded={!syncCollapsed}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {syncCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <Sparkles size={14} style={{ color: 'var(--accent)' }} />
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Last sync result</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--fg-3)' }}>
                  +{syncResult.createdCount} created · ~{syncResult.updatedCount} updated · {syncResult.skippedCount} skipped
                </span>
              </div>
            </button>
            {!syncCollapsed && (
              <div className="hf-card-body">
                {renderModelsSection('Created PPC models', syncResult.createdModels)}
                {renderModelsSection('Updated PPC models', syncResult.updatedModels)}
                {syncResult.skippedUsernames.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <h4 style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Skipped · {syncResult.skippedUsernames.length}
                    </h4>
                    <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 12.5 }}>
                      {syncResult.skippedUsernames.join(', ')}
                    </p>
                  </div>
                )}
                {syncResult.missingTags.length > 0 && (
                  <ErrorBanner
                    kind="warning"
                    title={`Missing tags · ${syncResult.missingTagsCount}`}
                    message={syncResult.missingTags.join(', ')}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ Global PPC Ordering ═══ */}
        <div className="hf-card flush">
          <div className="hf-card-header">
            <div>
              <h3 className="hf-card-title">Global PPC Ordering</h3>
              <span className="hf-help" style={{ marginTop: 2 }}>
                Default order for all pages · overridden by page-specific positions.
              </span>
            </div>
            <button
              className="hf-btn primary"
              onClick={handleSavePpcOrder}
              disabled={ppcSaving || !ppcDirty}
              title={ppcDirty ? 'Save reordering' : 'No changes to save'}
            >
              {ppcSaving
                ? <><Loader2 size={14} className="spin" /> Saving…</>
                : <><Save size={14} /> {ppcDirty ? 'Save order' : 'Saved'}</>}
            </button>
          </div>

          <div className="hf-tabstrip" style={{ padding: '0 16px' }}>
            {(['REGULAR', 'TS'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setPpcTab(tab)}
                className={ppcTab === tab ? 'on' : ''}
                aria-current={ppcTab === tab}
              >
                {tab} <span className="ct">{ppcCounts[tab]}</span>
              </button>
            ))}
          </div>

          <div className="hf-card-body flush">
            {ppcLoading ? (
              <table className="hf-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th style={{ width: 48 }} className="num">#</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th style={{ width: 80 }} className="num">Order</th>
                    <th style={{ width: 130 }}>PPC Type</th>
                  </tr>
                </thead>
                <tbody>
                  <TableSkeleton rows={6} columns={[4, 6, 22, 28, 8, 14]} numericColumns={[1, 4]} />
                </tbody>
              </table>
            ) : filteredPpcModels.length === 0 ? (
              <div style={{ padding: 16 }}>
                <EmptyState
                  Icon={DollarSign}
                  title={`No ${ppcTab} models`}
                  description={
                    ppcModels.length === 0
                      ? 'Run "Sync PPC models" to import the current PPC roster from base.'
                      : `Switch to the ${ppcTab === 'REGULAR' ? 'TS' : 'REGULAR'} tab — there are no ${ppcTab} models yet.`
                  }
                  action={
                    ppcModels.length === 0 && (
                      <button className="hf-btn primary" onClick={handleSync} disabled={syncLoading}>
                        <DollarSign size={13} /> Sync PPC models
                      </button>
                    )
                  }
                />
              </div>
            ) : (
              <div className="hf-scroll-x">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handlePpcDragEnd}
                >
                  <SortableContext
                    items={filteredPpcModels.map((m) => String(m.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="hf-table" style={{ minWidth: 640 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th style={{ width: 48 }} className="num">#</th>
                          <th>Username</th>
                          <th>Name</th>
                          <th style={{ width: 80 }} className="num">Order</th>
                          <th style={{ width: 130 }}>PPC Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPpcModels.map((model, index) => (
                          <SortablePpcRow
                            key={model.id}
                            model={model}
                            index={index}
                            onPpcTypeChange={handlePpcTypeChange}
                            isUpdating={updating}
                          />
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default PpcPage;
